import React, { useEffect, useState, useContext } from 'react';
import { X, User, Lock, Loader2 } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { authService } from '../../services/auth';
import { ToastContext } from '../../App';
import { formatBrazilianPhoneNumber } from '../../utils/fieldCache';
import { LoginModalContext } from '../../App';
// Add a helper function to manage profile data in localStorage
const profileStorage = {
  getProfileData: () => {
    const data = localStorage.getItem('profileData');
    return data ? JSON.parse(data) : null;
  },
  setProfileData: data => {
    localStorage.setItem('profileData', JSON.stringify({
      ...data,
      timestamp: Date.now()
    }));
  },
  isDataFresh: () => {
    const data = profileStorage.getProfileData();
    if (!data || !data.timestamp) return false;
    // Consider data fresh if less than 5 minutes old
    const fiveMinutesInMs = 5 * 60 * 1000;
    return Date.now() - data.timestamp < fiveMinutesInMs;
  }
};
const ProfileModal = ({
  isOpen,
  onClose,
  user,
  onLogout,
  activeTab = 'profile',
  setActiveTab
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    telefone: '',
    empresaTipo: '',
    cargo: '',
    funcionariosQuantidade: '',
    urlSite: '',
    comoConheceu: '',
    pretendeContratarSeo: '',
    area_de_atuacao: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const {
    addToast
  } = useContext(ToastContext);
  const {
    openLoginModal
  } = useContext(LoginModalContext);
  useEffect(() => {
    if (isOpen) {
      // Reset error and success messages when modal is opened
      setError('');
      setSuccess('');
      // Load cached profile data
      const cachedData = profileStorage.getProfileData();
      if (cachedData) {
        setProfileData({
          firstname: cachedData.firstname || '',
          lastname: cachedData.lastname || '',
          email: cachedData.email || '',
          telefone: cachedData.telefone ? formatBrazilianPhoneNumber(cachedData.telefone) : '',
          empresaTipo: cachedData.empresaTipo || '',
          cargo: cachedData.cargo || '',
          funcionariosQuantidade: cachedData.funcionariosQuantidade || '',
          urlSite: cachedData.urlSite || '',
          comoConheceu: cachedData.comoConheceu || '',
          pretendeContratarSeo: cachedData.pretendeContratarSeo || '',
          area_de_atuacao: cachedData.area_de_atuacao || ''
        });
        // If data is not fresh, refresh it in background
        if (!profileStorage.isDataFresh()) {
          loadUserProfile(false);
        }
      } else {
        // If no cached data at all, show loading state and load from API
        setIsLoadingProfile(true);
        loadUserProfile(true);
      }
    }
  }, [isOpen]);
  const loadUserProfile = async (showLoading = true) => {
    if (showLoading) {
      setIsLoadingProfile(true);
    }
    try {
      const userData = await authService.getProfile();
      // Format and set the profile data
      const formattedData = {
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        email: userData.email || '',
        telefone: userData.telefone ? formatBrazilianPhoneNumber(userData.telefone) : '',
        empresaTipo: userData.empresaTipo || '',
        cargo: userData.cargo || '',
        funcionariosQuantidade: userData.funcionariosQuantidade || '',
        urlSite: userData.urlSite || '',
        comoConheceu: userData.comoConheceu || '',
        pretendeContratarSeo: userData.pretendeContratarSeo || '',
        area_de_atuacao: userData.area_de_atuacao || ''
      };
      setProfileData(formattedData);
      // Save to localStorage with timestamp
      profileStorage.setProfileData(userData);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      if (showLoading) {
        setError('Falha ao carregar o perfil do usuário');
      }
    } finally {
      if (showLoading) {
        setIsLoadingProfile(false);
      }
    }
  };
  if (!isOpen) return null;
  const handlePhoneChange = e => {
    const formattedPhone = formatBrazilianPhoneNumber(e.target.value);
    setProfileData({
      ...profileData,
      telefone: formattedPhone
    });
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      // Ensure URL starts with http:// or https://
      let urlSite = profileData.urlSite.trim();
      if (urlSite && !urlSite.match(/^https?:\/\//)) {
        urlSite = 'https://' + urlSite;
      }
      const response = await authService.updateProfile({
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        telefone: profileData.telefone.replace(/\D/g, ''),
        empresaTipo: profileData.empresaTipo,
        cargo: profileData.cargo,
        funcionariosQuantidade: profileData.funcionariosQuantidade,
        urlSite: urlSite,
        comoConheceu: profileData.comoConheceu,
        pretendeContratarSeo: profileData.pretendeContratarSeo,
        area_de_atuacao: profileData.area_de_atuacao
      });
      // Update the cached profile data
      if (response.data && response.data.user) {
        profileStorage.setProfileData(response.data.user);
      }
      // Close the modal
      onClose();
      // Show toast notification
      addToast('success', 'Perfil atualizado com sucesso!');
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.message).join(', ');
        setError(`Erro de validação: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Erro ao atualizar perfil. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handlePasswordChange = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    // Validação de senha completa
    if (passwordData.newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setIsLoading(false);
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('As senhas não conferem');
      setIsLoading(false);
      return;
    }
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Close the profile modal
      onClose();
      // Logout the user
      onLogout();
      // Show toast notification
      addToast('success', 'Senha alterada com sucesso! Por favor, faça login novamente.');
      // Open login modal with relogin flag set to true
      setTimeout(() => {
        openLoginModal(true);
      }, 300);
    } catch (error) {
      setError(error.response?.data?.message || 'Erro ao alterar senha. Verifique a senha atual.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-id="element-92">
      <div className="relative w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl h-[650px] max-h-[90vh] flex flex-col overflow-hidden" data-id="element-93">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" aria-label="Close" disabled={isLoading} data-id="element-94">
          <X size={20} data-id="element-95" />
        </button>
        <div className="flex items-center space-x-4 mb-6" data-id="element-96">
          <UserAvatar user={user} data-id="element-97" />
          <div data-id="element-98">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-99">
              {profileData.firstname} {profileData.lastname}
            </h2>
            <p className="text-sm text-gray-600" data-id="element-100">
              {profileData.email || user.email}
            </p>
          </div>
        </div>
        {isLoadingProfile ? <div className="flex-grow flex justify-center items-center" data-id="element-101">
            <Loader2 size={24} className="animate-spin text-[#1649ff]" data-id="element-102" />
          </div> : <div className="flex-grow flex flex-col overflow-hidden" data-id="element-103">
            <div className="flex border-b border-gray-200 mb-6" data-id="element-104">
              <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'profile' ? 'border-[#1649ff] text-[#1649ff]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('profile')} disabled={isLoading} data-id="element-105">
                <User size={16} className="mr-2" data-id="element-106" />
                Perfil
              </button>
              <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'security' ? 'border-[#1649ff] text-[#1649ff]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('security')} disabled={isLoading} data-id="element-107">
                <Lock size={16} className="mr-2" data-id="element-108" />
                Segurança
              </button>
            </div>
            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md" data-id="element-109">
                {error}
              </div>}
            {success && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md" data-id="element-110">
                {success}
              </div>}
            <div className="flex-grow overflow-y-auto px-1" data-id="element-111">
              {activeTab === 'profile' ? <form onSubmit={handleSubmit} className="space-y-4 px-1" data-id="element-112">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-113">
                    <div data-id="element-114">
                      <label htmlFor="firstname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-115">
                        Nome
                      </label>
                      <input type="text" id="firstname" value={profileData.firstname} onChange={e => setProfileData({
                  ...profileData,
                  firstname: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-116" />
                    </div>
                    <div data-id="element-117">
                      <label htmlFor="lastname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-118">
                        Sobrenome
                      </label>
                      <input type="text" id="lastname" value={profileData.lastname} onChange={e => setProfileData({
                  ...profileData,
                  lastname: e.target.value
                })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-119" />
                    </div>
                  </div>
                  <div data-id="element-120">
                    <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-121">
                      Email
                    </label>
                    <input type="email" id="email" name="email" value={profileData.email} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] bg-gray-50 shadow-sm text-gray-900" disabled autoComplete="username" data-id="element-122" />
                  </div>
                  <div data-id="element-123">
                    <label htmlFor="telefone" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-124">
                      Telefone
                    </label>
                    <input type="tel" id="telefone" value={profileData.telefone} onChange={handlePhoneChange} placeholder="(11) 99999-9999" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} autoComplete="tel" data-id="element-125" />
                  </div>
                  <div data-id="element-126">
                    <label htmlFor="urlSite" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-127">
                      URL do site
                    </label>
                    <input type="url" id="urlSite" name="url" value={profileData.urlSite} onChange={e => setProfileData({
                ...profileData,
                urlSite: e.target.value
              })} placeholder="https://www.example.com" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} autoComplete="url" data-id="element-128" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-129">
                      A URL deve começar com http:// ou https://
                    </p>
                  </div>
                  <div data-id="element-130">
                    <label htmlFor="empresaTipo" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-131">
                      Segmento
                    </label>
                    <select id="empresaTipo" value={profileData.empresaTipo} onChange={e => setProfileData({
                ...profileData,
                empresaTipo: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-132">
                      <option value="" disabled data-id="element-133">
                        Selecione o segmento
                      </option>
                      <option value="Agência / Consultoria" data-id="element-134">
                        Agência / Consultoria
                      </option>
                      <option value="Agronegócio" data-id="element-135">Agronegócio</option>
                      <option value="Autoconhecimento" data-id="element-136">Autoconhecimento</option>
                      <option value="Automotiva" data-id="element-137">Automotiva</option>
                      <option value="Bens de Consumo / Alimentos" data-id="element-138">
                        Bens de Consumo / Alimentos
                      </option>
                      <option value="E-commerce" data-id="element-139">E-commerce</option>
                      <option value="Educação" data-id="element-140">Educação</option>
                      <option value="Empresa pública / ONGs" data-id="element-141">
                        Empresa pública / ONGs
                      </option>
                      <option value="Energia" data-id="element-142">Energia</option>
                      <option value="Eventos" data-id="element-143">Eventos</option>
                      <option value="Financeiro" data-id="element-144">Financeiro</option>
                      <option value="Imobiliário" data-id="element-145">Imobiliário</option>
                      <option value="Indústria" data-id="element-146">Indústria</option>
                      <option value="Infoprodutos" data-id="element-147">Infoprodutos</option>
                      <option value="Mídia comunicação" data-id="element-148">
                        Mídia comunicação
                      </option>
                      <option value="Mobilidade" data-id="element-149">Mobilidade</option>
                      <option value="Moda / Vestuário" data-id="element-150">Moda / Vestuário</option>
                      <option value="Religião / Espiritualidade" data-id="element-151">
                        Religião / Espiritualidade
                      </option>
                      <option value="Saúde / Beleza / Bem-Estar" data-id="element-152">
                        Saúde / Beleza / Bem-Estar
                      </option>
                      <option value="Serviços gerais" data-id="element-153">Serviços gerais</option>
                      <option value="Tecnologia / SaaS" data-id="element-154">
                        Tecnologia / SaaS
                      </option>
                      <option value="Telecomunicações" data-id="element-155">Telecomunicações</option>
                      <option value="Transporte / Logística" data-id="element-156">
                        Transporte / Logística
                      </option>
                      <option value="Turismo / Lazer" data-id="element-157">Turismo / Lazer</option>
                      <option value="Varejo" data-id="element-158">Varejo</option>
                      <option value="Outro" data-id="element-159">Outro</option>
                    </select>
                  </div>
                  <div data-id="element-160">
                    <label htmlFor="area_de_atuacao" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-161">
                      Área de atuação
                    </label>
                    <select id="area_de_atuacao" value={profileData.area_de_atuacao} onChange={e => setProfileData({
                ...profileData,
                area_de_atuacao: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} data-id="element-162">
                      <option value="" disabled data-id="element-163">
                        Selecione a área
                      </option>
                      <option value="Administrativo" data-id="element-164">Administrativo</option>
                      <option value="Comercial" data-id="element-165">Comercial</option>
                      <option value="Contábil" data-id="element-166">Contábil</option>
                      <option value="Customer Success" data-id="element-167">Customer Success</option>
                      <option value="E-commerce" data-id="element-168">E-commerce</option>
                      <option value="Financeiro" data-id="element-169">Financeiro</option>
                      <option value="Marketing" data-id="element-170">Marketing</option>
                      <option value="Operação" data-id="element-171">Operação</option>
                      <option value="RH" data-id="element-172">RH</option>
                      <option value="SEO" data-id="element-173">SEO</option>
                      <option value="Tecnologia" data-id="element-174">Tecnologia</option>
                      <option value="Outra área" data-id="element-175">Outra área</option>
                    </select>
                  </div>
                </form> : <form onSubmit={handlePasswordChange} className="space-y-4 px-1" data-id="element-176">
                  {/* Hidden email field to associate with the password change */}
                  <input type="email" name="email" value={profileData.email} autoComplete="username" className="hidden" readOnly data-id="element-177" />
                  <div data-id="element-178">
                    <label htmlFor="currentPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-179">
                      Senha atual
                    </label>
                    <input type="password" id="currentPassword" name="current-password" value={passwordData.currentPassword} onChange={e => setPasswordData({
                ...passwordData,
                currentPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required autoComplete="current-password" data-id="element-180" />
                  </div>
                  <div data-id="element-181">
                    <label htmlFor="newPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-182">
                      Nova senha
                    </label>
                    <input type="password" id="newPassword" name="new-password" value={passwordData.newPassword} onChange={e => setPasswordData({
                ...passwordData,
                newPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required minLength={6} autoComplete="new-password" data-id="element-183" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-184">
                      Mínimo de 6 caracteres
                    </p>
                  </div>
                  <div data-id="element-185">
                    <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-186">
                      Confirmar nova senha
                    </label>
                    <input type="password" id="confirmPassword" name="confirm-password" value={passwordData.confirmPassword} onChange={e => setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] shadow-sm text-gray-900" disabled={isLoading} required minLength={6} autoComplete="new-password" data-id="element-187" />
                  </div>
                </form>}
            </div>
            <div className="mt-6 pt-4" data-id="element-188">
              {activeTab === 'profile' ? <button onClick={handleSubmit} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-189">
                  {isLoading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-190" />
                      Salvando...
                    </> : 'Salvar alterações'}
                </button> : <button onClick={handlePasswordChange} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-191">
                  {isLoading ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-192" />
                      Alterando...
                    </> : 'Alterar senha'}
                </button>}
            </div>
          </div>}
      </div>
    </div>;
};
export default ProfileModal;